(() => {
  if (window.themeServiceProductBound) return;
  window.themeServiceProductBound = true;
  const init = (root) => {
    if (!root || root.dataset.erAuraReady === 'true') return;
    root.dataset.erAuraReady = 'true';

    const image = root.querySelector('.erservice__image:not(.erservice__placeholder)');
    if (!image) return;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const rgbToHsl = ([r, g, b]) => {
      r /= 255;
      g /= 255;
      b /= 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0;
      let s = 0;
      const l = (max + min) / 2;
      const d = max - min;

      if (d !== 0) {
        s = l > .5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
          case g:
            h = ((b - r) / d + 2) / 6;
            break;
          default:
            h = ((r - g) / d + 4) / 6;
        }
      }

      return [h, s, l];
    };

    const hslToRgb = ([h, s, l]) => {
      if (s === 0) {
        const value = Math.round(l * 255);
        return [value, value, value];
      }

      const hueToRgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < .5 ? l * (1 + s) : l + s - (l * s);
      const p = (2 * l) - q;

      return [
        Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
        Math.round(hueToRgb(p, q, h) * 255),
        Math.round(hueToRgb(p, q, h - 1 / 3) * 255)
      ];
    };

    const vivid = (rgb) => {
      const hsl = rgbToHsl(rgb);
      hsl[1] = clamp(Math.max(hsl[1] * 1.35, .62), 0, .94);
      hsl[2] = clamp(hsl[2], .36, .61);
      return hslToRgb(hsl);
    };

    const distance = (a, b) => {
      const dr = a[0] - b[0];
      const dg = a[1] - b[1];
      const db = a[2] - b[2];
      return Math.sqrt((dr * dr) + (dg * dg) + (db * db));
    };

    const mix = (a, b, amount) => [
      Math.round(a[0] + ((b[0] - a[0]) * amount)),
      Math.round(a[1] + ((b[1] - a[1]) * amount)),
      Math.round(a[2] + ((b[2] - a[2]) * amount))
    ];

    const extractPalette = (source) => {
      if (!source || !source.naturalWidth || !source.naturalHeight) return null;

      const canvas = document.createElement('canvas');
      canvas.width = 48;
      canvas.height = 48;

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;

      try {
        context.drawImage(source, 0, 0, 48, 48);
        const pixels = context.getImageData(0, 0, 48, 48).data;
        const buckets = new Map();

        for (let i = 0; i < pixels.length; i += 4) {
          if (pixels[i + 3] < 170) continue;

          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const sat = max === 0 ? 0 : (max - min) / max;
          const lum = (.2126 * r) + (.7152 * g) + (.0722 * b);

          if (lum < 16 || lum > 242) continue;

          const qr = Math.round(r / 30) * 30;
          const qg = Math.round(g / 30) * 30;
          const qb = Math.round(b / 30) * 30;
          const key = `${qr},${qg},${qb}`;
          const existing = buckets.get(key) || {
            score: 0,
            count: 0,
            r: 0,
            g: 0,
            b: 0
          };

          existing.score += .4 + (sat * 2.2);
          existing.count += 1;
          existing.r += r;
          existing.g += g;
          existing.b += b;
          buckets.set(key, existing);
        }

        const ranked = [...buckets.values()]
          .filter((bucket) => bucket.count >= 2)
          .map((bucket) => ({
            score: bucket.score,
            color: vivid([
              Math.round(bucket.r / bucket.count),
              Math.round(bucket.g / bucket.count),
              Math.round(bucket.b / bucket.count)
            ])
          }))
          .sort((a, b) => b.score - a.score);

        if (!ranked.length) return null;

        const primary = ranked[0].color;
        let secondary = primary;

        for (const candidate of ranked.slice(1)) {
          if (distance(primary, candidate.color) >= 66) {
            secondary = candidate.color;
            break;
          }
        }

        if (distance(primary, secondary) < 32) {
          const hsl = rgbToHsl(primary);
          hsl[0] = (hsl[0] + .13) % 1;
          secondary = hslToRgb(hsl);
        }

        return { primary, secondary };
      } catch (error) {
        return null;
      }
    };

    const applyPalette = (palette) => {
      if (!palette) return;

      const midpoint = mix(palette.primary, palette.secondary, .5);
      root.style.setProperty('--ers-primary', palette.primary.join(', '));
      root.style.setProperty('--ers-secondary', palette.secondary.join(', '));
      root.style.setProperty('--ers-mid', midpoint.join(', '));
      root.dataset.erPaletteReady = 'true';
    };

    const sample = () => {
      try {
        const direct = extractPalette(image);
        if (direct) {
          applyPalette(direct);
          return;
        }
      } catch (error) {
        /* Fall through to an anonymous image request. */
      }

      const source = image.currentSrc || image.src;
      if (!source) return;

      const sampler = new Image();
      sampler.crossOrigin = 'anonymous';
      sampler.decoding = 'async';
      sampler.onload = () => applyPalette(extractPalette(sampler));
      sampler.src = source;
    };

    if (image.complete && image.naturalWidth > 0) {
      sample();
    } else {
      image.addEventListener('load', sample, { once: true });
    }



  };
  const scan = (scope = document) => scope.querySelectorAll('[data-er-service-product]').forEach(init);
  scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
